import React, { useState } from 'react';
import { StateInfo } from 'services/system';
import { CompanyUserSignupRequest } from 'services/signup';

export function SelfSignupForm(props: SelfSignupFormProps) {
  const { statesInfo } = props;
  const [formData, setFormData] = useState<CompanyUserSignupRequest>({
    firstName: '',
    lastName: '',
    title: '',
    workPhone: '',
    email: '',
    password: '',
    companyName: '',
    siteName: '',
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zipCode: '',
    sitePlanProgram: 'federal',
    siteType: 'non-construction',
    receiveEmails: false,
    receiveMails: false,
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showSuccessfulSignup, setShowSuccessfulSignup] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? event.target.checked : value,
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const response = await fetch('/user/signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    });
    if (response.status === 200) {
      setShowSuccessfulSignup(true);
    }
  };

  const isFormFilled = (): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { addressTwo , ...dataToCheck } = formData;
    return !Object.values(dataToCheck).some(input => input === '');
  };

  return <>
    <div className='is-flex is-flex-direction-column is-align-items-center mb-4'>
      <img
        className='self-signup-logo'
        src='/images/vpp_online_logo_large.png'
        title='VPP Online'
        onClick={() => window.location.href = '/'}
      />
      <div
        className='content signup-header-content has-text-weight-medium mb-2'
      >
        VPP Online® helps you organize your EHS&S initiatives and makes it easier to conduct self-assessments,
        such as OSHA’s VPP Annual Self-Evaluation report. The cloud-based platform enables safety teams to collaborate
        in real-time and is designed to be used by any company or site that is committed to strengthening their
        EHS&S program. VPPPA Members get free access as part of their membership.
      </div>
      <div className='content has-text-weight-medium'>
        <a
          className='ml-2'
          href='https://link.auditsoft.co/vpp-online'
          target='_blank'
        >
         Learn about VPP Online
        </a>
      </div>
    </div>
    {
      showSuccessfulSignup
        ? <div className='is-flex is-justify-content-center is-align-items-center'>
          <div className='message is-success'>
            <div className='message-header'>
              <p>Success</p>
            </div>
            <div className='message-body'>
              Sign-up successful! Please check your email to activate your account.
            </div>
          </div>
        </div>
        : <form onSubmit={handleSubmit}>
          <h2 className='title'>About You</h2>
          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>First Name</label>
                <div className='control'>
                  <input
                    className='input'
                    type='text'
                    required
                    placeholder='Enter first name'
                    name='firstName'
                    pattern='^[a-zA-Z\s]*$'
                    title='Enter your first name (No numbers or special characters)'
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>Last Name</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter last name'
                    name='lastName'
                    pattern='^[a-zA-Z\s]*$'
                    title='Enter your last name (No numbers or special characters)'
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>Title</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter title'
                    name='title'
                    value={formData.title}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>Work Phone</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter phone number'
                    name='workPhone'
                    pattern='(\d.*){10}'
                    title='Enter your 10-digit phone number'
                    value={formData.workPhone}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>Email</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='email'
                    placeholder='Enter email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>Password</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='password'
                    placeholder='Enter password'
                    name='password'
                    pattern='.{10,}'
                    title='Enter a strong password (Minimum 10 characters)'
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <h2 className='title mb-2'>
          About Your Organization
          </h2>
          <div className='is-flex is-align-items-center mb-3'>
            <p className='has-text-weight-semibold'>
              <i className='fas fa-info-circle is-size-6 mr-1'></i>
            If your organization has multiple sites, please enter the details of any participating site. You’ll be able to add the rest after signing up.
            </p>
          </div>
          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>Organization Name</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter organization name'
                    name='companyName'
                    value={formData.companyName}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>Site Name</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter site name'
                    value={formData.siteName}
                    name='siteName'
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>Address 1</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter address 1'
                    value={formData.addressOne}
                    name='addressOne'
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>Address 2</label>
                <div className='control'>
                  <input
                    className='input'
                    type='text'
                    placeholder='Enter address 2'
                    value={formData.addressTwo}
                    name='addressTwo'
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>City</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter city'
                    value={formData.city}
                    name='city'
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>State</label>
                <div className='control'>
                  <div className='select is-fullwidth'>
                    <select
                      name='state'
                      required
                      value={formData.state}
                      onChange={handleChange}
                    >
                      <option value=''>Enter state</option>
                      {
                        statesInfo.map(stateInfo => <option key={stateInfo.stateId} value={stateInfo.stateId}>{stateInfo.name}</option>)
                      }
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>ZIP Code</label>
                <div className='control'>
                  <input
                    className='input'
                    required
                    type='text'
                    placeholder='Enter ZIP Code'
                    value={formData.zipCode}
                    name='zipCode'
                    pattern='^\d{5}(-\d{4})?$'
                    title='ZIP Code (Format: 12345 or 12345-6789)'
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className='column'></div>
          </div>

          <div className='columns'>
            <div className='column'>
              <div className='field'>
                <label className='label'>
                Does this site use the federal or state-plan program?
                </label>
                <div className='control'>
                  <div className='select is-fullwidth'>
                    <select
                      name='sitePlanProgram'
                      required
                      value={formData.sitePlanProgram}
                      onChange={handleChange}
                    >
                      <option value='federal'>Federal Plan Program</option>
                      <option value='state'>State Plan Program</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className='column'>
              <div className='field'>
                <label className='label'>
                Is this site a Fixed site or Mobile workforce?
                </label>
                <div className='control'>
                  <div className='select is-fullwidth'>
                    <select
                      name='siteType'
                      required
                      value={formData.siteType}
                      onChange={handleChange}
                    >
                      <option value='non-construction'>Non construction fixed site</option>
                      <option value='construction'>Construction</option>
                      <option value='mobile'>Mobile Workforce</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='field'>
            <div className='control'>
              <label className='checkbox'>
                <input
                  type='checkbox'
                  name='receiveEmails'
                  className='mr-2'
                  checked={formData.receiveEmails}
                  onChange={handleChange}
                />
              I would like to receive email communications regarding my VPP Online account.
              </label>
            </div>
          </div>
          <div className='field'>
            <div className='control'>
              <label className='checkbox'>
                <input
                  type='checkbox'
                  name='receiveMails'
                  className='mr-2'
                  checked={formData.receiveMails}
                  onChange={handleChange}
                />
              I would like to receive direct mail communications regarding my VPP Online account.
              </label>
            </div>
          </div>
          <div className='field'>
            <div className='control'>
              <label className='checkbox'>
                <input
                  type='checkbox'
                  checked={acceptTerms}
                  className='mr-2'
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                />
              I confirm that I have read and agree to the
                <a target='_blank' href='https://link.auditsoft.co/vpp-online-terms'>
                  {` Terms & Conditions`}
                </a>.
              </label>
            </div>
          </div>
          <div className='columns'>
            <div className='column is-one-third'>
              <div className='field'>
                <div className='control'>
                  <button type='submit' className='button is-primary is-fullwidth' disabled={!acceptTerms || !isFormFilled()}>
                  Sign up
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
    }
  </>;
}

type SelfSignupFormProps = {
  statesInfo: StateInfo[];
};